import { AdConsentModal } from './AdConsentModal';
import { DataDisclosureModal } from './DataDisclosureModal';
import { LegalConsentModal } from './LegalConsentModal';
import { LegalDocumentModal } from './LegalDocumentModal';
import { OnboardingModal } from './OnboardingModal';

type ConsentFlowProps = {
  showOnboarding: boolean;
  showDataDisclosure: boolean;
  showAdConsent: boolean;
  showLegalConsent: boolean;
  legalDoc: 'privacy' | 'terms' | null;
  onboardingStep: number;
  onboardingSteps: Array<{
    title: string;
    body: string;
    highlights: string[];
  }>;
  labels: {
    onboarding: {
      welcome: string;
      skip: string;
      step: string;
      back: string;
      next: string;
      start: string;
    };
    disclosure: {
      title: string;
      body: string;
      localData: string;
      cloudBackup: string;
      billing: string;
      personalizedAds: string;
      continue: string;
      exit: string;
      requiredHint: string;
    };
    adConsent: {
      title: string;
      body: string;
      allow: string;
      keepNonPersonalized: string;
    };
    legalConsent: {
      title: string;
      body: string;
      privacy: string;
      terms: string;
      disclaimer: string;
      accept: string;
      exit: string;
    };
    legalDocument: {
      privacyTitle: string;
      termsTitle: string;
      privacyContent: string;
      termsContent: string;
      close: string;
      openExternal: string;
      lastUpdated: string;
    };
  };
  onChangeOnboardingStep: (step: number) => void;
  onSkipOnboarding: () => void;
  onCompleteOnboarding: () => void;
  onAcceptDisclosure: (choices: {
    localDataAck: boolean;
    cloudBackupAck: boolean;
    billingAck: boolean;
    personalizedAds: boolean;
  }) => void;
  onExitConsent: () => void;
  onAllowAds: () => void;
  onKeepNonPersonalizedAds: () => void;
  onOpenPrivacy: () => void;
  onOpenTerms: () => void;
  onAcceptLegal: () => void;
  onCloseLegalDoc: () => void;
  onOpenExternalLegal: () => void;
};

export function ConsentFlow({
  showOnboarding,
  showDataDisclosure,
  showAdConsent,
  showLegalConsent,
  legalDoc,
  onboardingStep,
  onboardingSteps,
  labels,
  onChangeOnboardingStep,
  onSkipOnboarding,
  onCompleteOnboarding,
  onAcceptDisclosure,
  onExitConsent,
  onAllowAds,
  onKeepNonPersonalizedAds,
  onOpenPrivacy,
  onOpenTerms,
  onAcceptLegal,
  onCloseLegalDoc,
  onOpenExternalLegal
}: ConsentFlowProps) {
  return (
    <>
      {showOnboarding && (
        <OnboardingModal
          steps={onboardingSteps}
          stepIndex={onboardingStep}
          labels={labels.onboarding}
          onChangeStep={onChangeOnboardingStep}
          onSkip={onSkipOnboarding}
          onComplete={onCompleteOnboarding}
        />
      )}

      {showDataDisclosure && (
        <DataDisclosureModal labels={labels.disclosure} onAccept={onAcceptDisclosure} onExit={onExitConsent} />
      )}

      {showAdConsent && (
        <AdConsentModal labels={labels.adConsent} onAllow={onAllowAds} onKeepNonPersonalized={onKeepNonPersonalizedAds} />
      )}

      {showLegalConsent && (
        <LegalConsentModal
          labels={labels.legalConsent}
          onOpenPrivacy={onOpenPrivacy}
          onOpenTerms={onOpenTerms}
          onAccept={onAcceptLegal}
          onExit={onExitConsent}
        />
      )}

      {legalDoc && (
        <LegalDocumentModal
          title={legalDoc === 'privacy' ? labels.legalDocument.privacyTitle : labels.legalDocument.termsTitle}
          content={legalDoc === 'privacy' ? labels.legalDocument.privacyContent : labels.legalDocument.termsContent}
          labels={{
            close: labels.legalDocument.close,
            openExternal: labels.legalDocument.openExternal,
            lastUpdated: labels.legalDocument.lastUpdated
          }}
          onClose={onCloseLegalDoc}
          onOpenExternal={onOpenExternalLegal}
        />
      )}
    </>
  );
}
